import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "./AuthContext";
import { updateProfile } from "./authService";
import { useNavigate } from "react-router-dom";
import ToastMessage from "../components/ToastMessage";

const UpdateProfilePage = () => {
  useEffect(() => {
    document.title = "Update Profile | Workgear365";
  }, []);

  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const billingStreetRef = useRef(null);
  const shippingStreetRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    contact_person: "",
    website: "",
    tax_id: "",
    payment_terms: "",
    notes: "",
    shipping_address: "",
    shipping_address2: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip_code: "",
    shipping_country: "USA",
  });

  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState("");
  const [toast, setToast] = useState(null); // <-- Toast state

  // Load user data
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        ...user,
      }));
    }
  }, [user]);

  // Sync shipping with billing if sameAsBilling
  useEffect(() => {
    if (sameAsBilling) {
      setForm((prev) => ({
        ...prev,
        shipping_address: prev.address,
        shipping_address2: prev.address2,
        shipping_city: prev.city,
        shipping_state: prev.state,
        shipping_zip_code: prev.zip_code,
        shipping_country: prev.country,
      }));
    }
  }, [sameAsBilling, form.address, form.city, form.state, form.zip_code, form.country]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (sameAsBilling && ["address","city","state","zip_code","country"].includes(name)) {
        next.shipping_address = next.address;
        next.shipping_address2 = next.address2;
        next.shipping_city = next.city;
        next.shipping_state = next.state;
        next.shipping_zip_code = next.zip_code;
        next.shipping_country = next.country;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccess("");

    try {
      const res = await updateProfile(form);
      setUser(res.data.user);
      localStorage.setItem("user", JSON.stringify(res.data.user));

      setSuccess("Profile updated successfully!");
      setToast({ type: "success", message: "Profile updated successfully!" });

      // Redirect after short delay
      setTimeout(() => {
        navigate("/");
      }, 1500);

    } catch (err) {
      let errorMessages = [];
      if (err.response?.data?.errors) {
        errorMessages = Object.values(err.response.data.errors).flat();
      } else if (err.response?.data?.message) {
        errorMessages = [err.response.data.message];
      } else {
        errorMessages = ["Profile update failed"];
      }

      setErrors(errorMessages);

      // Scroll to top on error
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="container py-5">
      {toast && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card shadow">
            <div className="card-header text-white">
              <h4 className="mb-0">Update Profile</h4>
            </div>
            <div className="card-body p-4">
              {errors.length > 0 && errors.map((err, idx) => (
                <div key={idx} className="alert alert-danger py-1">{err}</div>
              ))}
              {success && <div className="alert alert-success">{success}</div>}

              <form onSubmit={handleSubmit}>
                {/* Account Information */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Full Name</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} className="form-control" required/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Company Name</label>
                    <input type="text" name="company_name" value={form.company_name} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Email</label>
                    <input type="email" name="email" value={form.email} className="form-control" disabled/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Phone</label>
                    <input type="text" name="phone" value={form.phone} onChange={handleChange} className="form-control"/>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Contact Person</label>
                    <input type="text" name="contact_person" value={form.contact_person} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Website</label>
                    <input type="text" name="website" value={form.website} onChange={handleChange} className="form-control"/>
                  </div>
                </div>

                {/* Billing Address */}
                <h5 className="text-primary border-bottom pb-2 mb-3">Billing Address</h5>
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">Street Address</label>
                    <input type="text" name="address" ref={billingStreetRef} value={form.address} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">Address Line 2</label>
                    <input type="text" name="address2" value={form.address2} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">City</label>
                    <input type="text" name="city" value={form.city} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">State</label>
                    <input type="text" name="state" value={form.state} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">ZIP</label>
                    <input type="text" name="zip_code" value={form.zip_code} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Country</label>
                    <input type="text" name="country" value={form.country} onChange={handleChange} className="form-control"/>
                  </div>
                </div>

                {/* Shipping Address */}
                <h5 className="text-primary border-bottom pb-2 mb-3">Shipping Address</h5>
                <div className="row">
                  <div className="col-12 mb-2">
                    <div className="form-check">
                      <input type="checkbox" className="form-check-input" checked={sameAsBilling} onChange={() => setSameAsBilling(s => !s)}/>
                      <label className="form-check-label">Same as billing</label>
                    </div>
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">Street Address</label>
                    <input type="text" name="shipping_address" ref={shippingStreetRef} value={form.shipping_address} onChange={handleChange} className="form-control" disabled={sameAsBilling}/>
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">Street Address Line 2</label>
                    <input type="text" name="shipping_address2" ref={shippingStreetRef} placeholder="Suite Or Box #" value={form.shipping_address2} onChange={handleChange} className="form-control" disabled={sameAsBilling}/>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">City</label>
                    <input type="text" name="shipping_city" value={form.shipping_city} onChange={handleChange} className="form-control" disabled={sameAsBilling}/>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">State</label>
                    <input type="text" name="shipping_state" value={form.shipping_state} onChange={handleChange} className="form-control" disabled={sameAsBilling}/>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">ZIP</label>
                    <input type="text" name="shipping_zip_code" value={form.shipping_zip_code} onChange={handleChange} className="form-control" disabled={sameAsBilling}/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Country</label>
                    <input type="text" name="shipping_country" value={form.shipping_country} onChange={handleChange} className="form-control" disabled={sameAsBilling}/>
                  </div>
                </div>

                {/* Additional Info */}
                <h5 className="text-primary border-bottom pb-2 mb-3">Additional Information</h5>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Tax ID</label>
                    <input type="text" name="tax_id" value={form.tax_id} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">Payment Terms</label>
                    <input type="text" name="payment_terms" value={form.payment_terms} onChange={handleChange} className="form-control"/>
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">Notes</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} className="form-control" rows="3"/>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary btn-lg">Update Profile</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateProfilePage;
